// Helper Functions
// @ts-ignore
import { vat } from "../App.tsx";

const getBaseResellerPrice = (pcData: Compound): number => {
  if (pcData.price.resell) return pcData.price.resell;
  return pcData.price.without_vat;
};

/**
 * Base reseller purchase price for the active campaign, when Niposom funds
 * (part of) the discount. Falls back to the configured reseller price.
 * Optional components are NOT discounted — only this base is.
 */
const getPromotionalBaseResellerPrice = (pcData: Compound): number => {
  const promo = pcData.price.promotional_reseller_price;
  return promo != null && promo > 0 ? promo : getBaseResellerPrice(pcData);
};

const sumSelectedComponentDeltas = (pcData: Compound): number =>
  Object.values(pcData.selected_components || {}).reduce(
    (acc, component) => acc + (component?.cost || 0),
    0,
  );

const getBasePVPPrice = (pcData: Compound): number => {
  const promo = pcData.price.promotional_pvp_with_vat;
  return promo != null && promo > 0 ? promo : pcData.price.value;
};

const getCurrentConfigurationResellerPrice = (pcData: Compound): number =>
  sumSelectedComponentDeltas(pcData) + getPromotionalBaseResellerPrice(pcData);

/** Pre-campaign reseller price of the current configuration (for strikethrough). */
const getStandardConfigurationResellerPrice = (pcData: Compound): number =>
  sumSelectedComponentDeltas(pcData) + getBaseResellerPrice(pcData);

const hasCustomizationComponents = (pcData: Compound): boolean => {
  return Object.values(pcData.selected_components || {}).some(
    (component) => component && component.cost > 0,
  );
};

const excelFloor = (number: number, significance: number): number => {
  if (significance <= 0)
    throw new Error("Significance must be greater than zero");
  return Math.floor(number / significance) * significance;
};

const getResellerLevelMargin = (pcData: Compound): number =>
  pcData.price.margin || 0;

const getCurrentResellerGrossMargin = (pcData: Compound): number => {
  // Campaign-aware: the reseller's margin is derived from the promotional PVP
  // and the promotional (Niposom-funded) reseller price.
  const resellerPrice = getPromotionalBaseResellerPrice(pcData);
  const pvpPrice = getBasePVPPrice(pcData);
  return pvpPrice / (1 + pcData.price.vat_rate) - resellerPrice;
};

const calculateResellerMarginPercentage = (pcData: Compound): number => {
  const grossMargin = getCurrentResellerGrossMargin(pcData);
  const resellerPrice = getPromotionalBaseResellerPrice(pcData);
  if (!(resellerPrice > 0)) return 0;
  return grossMargin / resellerPrice;
};

const calculatePVPPriceUsingMargin = (
  resellerPrice: number,
  marginPercentage: number,
  vat_rate: number,
): number => {
  return toDecimal(
    resellerPrice * (1 + marginPercentage / 100) * (1 + vat_rate),
  );
};

const calculateBaseConfigurationPVPPriceUsingCustomMarginPercentage = (
  pcData: Compound,
  marginPercentage: number,
): number => {
  const promoPvp = pcData.price.promotional_pvp_with_vat;
  const promoResell = pcData.price.promotional_reseller_price;
  // Campaign prices are not on the catalog's 5€ grid, so re-flooring snaps the
  // PVP to the wrong price. Scale the promo PVP proportionally to the margin
  // change instead: at the campaign margin this reproduces the promo PVP exactly.
  if (promoPvp != null && promoPvp > 0) {
    const initialMargin = calculateResellerMarginPercentage(pcData) * 100;
    if (1 + initialMargin / 100 > 0) {
      return toDecimal(
        (promoPvp * (1 + marginPercentage / 100)) / (1 + initialMargin / 100),
      );
    }
    if (promoResell != null && promoResell > 0) {
      return toDecimal(
        promoResell *
          (1 + marginPercentage / 100) *
          (1 + pcData.price.vat_rate),
      );
    }
  }
  const value =
    excelFloor(
      calculatePVPPriceUsingMargin(
        getPromotionalBaseResellerPrice(pcData),
        marginPercentage,
        pcData.price.vat_rate,
      ) + 1,
      5,
    ) - 1;
  return value;
};

const calculateCurrentConfigurationPVPPriceUsingCustomMarginPercentage = (
  pcData: Compound,
  marginPercentage: number | null,
): number => {
  if (marginPercentage === null) {
    // If marginPercentage is null, return the current configuration reseller price because it's the default pvp price
    return toInteger(getCurrentConfigurationResellerPrice(pcData));
  } else {
    return toInteger(
      calculatePVPPriceUsingMargin(
        getCurrentConfigurationResellerPrice(pcData),
        marginPercentage,
        pcData.price.vat_rate,
      ),
    );
  }
};

const calculateCurrentConfigurationGrossMarginUsingCustomMarginPercentage = (
  pcData: Compound,
  marginPercentage: number,
): number => {
  const pvpPrice =
    calculateCurrentConfigurationPVPPriceUsingCustomMarginPercentage(
      pcData,
      marginPercentage,
    );
  const resellerPrice = getCurrentConfigurationResellerPrice(pcData);
  return toDecimal(pvpPrice / (1 + pcData.price.vat_rate) - resellerPrice);
};

const calculateNewMarginPercentage = (
  resellerPrice: number,
  newPVP: number,
  vat_rate: number,
): number => {
  const margin = newPVP / (1 + vat_rate) - resellerPrice;
  return toPercentage(margin / resellerPrice);
};

const calculateNewMarginPercentageBasedOnBasePVP = (
  pcData: Compound,
  newPVP: number,
): number =>
  calculateNewMarginPercentage(
    getPromotionalBaseResellerPrice(pcData),
    newPVP,
    pcData.price.vat_rate,
  );

const calculateNewMarginPercentageBasedOnConfigurationPVP = (
  pcData: Compound,
  newPVP: number,
): number =>
  calculateNewMarginPercentage(
    getCurrentConfigurationResellerPrice(pcData),
    newPVP,
    pcData.price.vat_rate,
  );

/** Converts a decimal ratio (0..1) to a percentage display value (0..100).
 *  If the value is already a percentage, display it directly without calling this. */
const toPercentage = (value: number): number => Math.round(value * 10000) / 100;

const toDecimal = (value: number): number => Math.round(value * 100) / 100;

const toInteger = (value: number): number => Math.round(value);

/**
 * Returns the reseller-side price (no VAT) of a PCComponent, falling back
 * across the available Price fields in the same priority order used by
 * getBaseResellerPrice. Returns 0 when the input is undefined.
 *
 * This is the unit that getCurrentConfigurationResellerPrice expects when
 * summing component deltas — keeping units consistent is essential.
 */
/**
 * Computes the reseller-no-VAT delta of `selected` against the group base.
 *
 * The backend's `adjustComponentPricesUsingBaseComponentAsReference` already
 * delta-encodes `price.value` (configuredCompoundResellerPrice) against the
 * group's base component — the base is 0, an upgrade carries a positive delta,
 * a cheaper option a negative one, and quantity is already factored in. So the
 * reseller cost delta is simply `selected.price.value`.
 *
 * `price.resell` (rev3) is the undiscounted per-unit reseller list price and
 * must NOT be used here: the base cost `getBaseResellerPrice` is itself the
 * compound (discounted) reseller price, so summing `value` deltas keeps the
 * units consistent (regression: a GT730 whose rev3 is 54.90 but compound price
 * is 48.37 was shown as +54.90 instead of +48.37).
 *
 * Used by CompoundDetailPage.handleChangeComponent to populate
 * selected_components[type].cost in the same unit that
 * getCurrentConfigurationResellerPrice consumes.
 */
const calculateComponentSelectionDelta = (
  _componentsForType: PCComponent[] | undefined,
  selected: PCComponent | undefined,
): number => {
  if (!selected) return 0;
  return selected.price?.value ?? 0;
};

/**
 * Resolves the price-reference (base) option of a component group for PVP
 * delta calculations. Priority — identical to the storefront's
 * getConfigurableComponentGroups:
 *   1. the real `baseComponent` flag (base groups: processador, motherboard…)
 *   2. the synthetic `empty_option` sentinel at €0 (peripheral groups:
 *      monitor, teclado, rato… meaning "nothing chosen yet")
 *   3. the first option as a last resort.
 *
 * Note: the intranet mutates `selected` onto the user's chosen option in
 * CompoundDetailPage.handleChangeComponent, so `find(c => c.selected)` must
 * NOT be used here (it would resolve to the selection, not the base).
 */
const getComponentGroupBase = (
  options: PCComponent[] | undefined,
): PCComponent | undefined => {
  if (!options || options.length === 0) return undefined;
  return (
    options.find((c) => c.baseComponent) ??
    options.find((c) => c.id === "empty_option") ??
    options[0]
  );
};

/**
 * Customer-facing PVP upgrade delta for the current configuration, computed
 * from the catalog `pvp_with_vat` field exactly like the eurosys storefront
 * (lib/storefront/compound.ts → calculateConfiguredPrice):
 *
 *   delta = Σ_groups ( selected.pvp_with_vat − base.pvp_with_vat )
 *
 * Component `pvp_with_vat` is delta-encoded against the group base (base = 0),
 * so an upgrade adds its positive delta and a cheaper option subtracts. The
 * delta is intentionally NOT scaled by the reseller margin slider nor by
 * quantity — only the base compound price reacts to margin, mirroring the
 * storefront where upgrades are added at their full PVP delta on top of the
 * (possibly promotional) base price.
 *
 * Iterates `selected_components` (the intranet's source of truth for what the
 * user picked); types absent from it resolve to base → 0 contribution.
 * Returns a raw (unrounded) number so the displayed/stored value matches the
 * storefront to full precision; formatting rounds at the presentation layer.
 */
const getConfiguredPVPUpgradeDelta = (pcData: Compound): number => {
  return Object.entries(pcData.selected_components || {}).reduce(
    (delta, [type, entry]) => {
      if (!entry?.id) return delta;
      const options = pcData.components?.[type];
      const selected = options?.find((c) => c.id === entry.id);
      if (!selected) return delta;
      const base = getComponentGroupBase(options);
      const selectedPVP = selected.price?.pvp_with_vat ?? 0;
      const basePVP = base?.price?.pvp_with_vat ?? 0;
      return delta + (selectedPVP - basePVP);
    },
    0,
  );
};

export {
  getBaseResellerPrice,
  getPromotionalBaseResellerPrice,
  getBasePVPPrice,
  getCurrentConfigurationResellerPrice,
  getStandardConfigurationResellerPrice,
  calculateResellerMarginPercentage,
  getCurrentResellerGrossMargin,
  calculateBaseConfigurationPVPPriceUsingCustomMarginPercentage,
  calculateCurrentConfigurationPVPPriceUsingCustomMarginPercentage,
  calculateCurrentConfigurationGrossMarginUsingCustomMarginPercentage,
  calculateNewMarginPercentageBasedOnBasePVP,
  calculateNewMarginPercentageBasedOnConfigurationPVP,
  calculateComponentSelectionDelta,
  getConfiguredPVPUpgradeDelta,
  toPercentage,
  toDecimal,
  toInteger,
  excelFloor,
};
